import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { Wave } from '../../types';
import authService from '../../services/authService';
import analyticsService from '../../services/analyticsService';

interface AudioProcessorProps {
  waves: Wave[];
  isVisible: boolean;
  onClose: () => void;
}

const generateWaveform = httpsCallable(functions, 'generateWaveform');

export const AudioProcessor: React.FC<AudioProcessorProps> = ({ 
  waves, 
  isVisible, 
  onClose 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(2);
  const [sampleRate, setSampleRate] = useState(44100);
  const [format, setFormat] = useState<'raw' | 'wav'>('wav');
  const [error, setError] = useState('');
  const [loopAudio, setLoopAudio] = useState(false);

  // Cleanup blob URL when component unmounts or audioUrl changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const handleGenerateAudio = async () => {
    if (!authService.isAuthenticated()) {
      setError('Please sign in to generate audio');
      analyticsService.trackError('audio_generate_unauthorized', 'User not authenticated');
      return;
    }

    if (waves.filter(w => !w.muted).length === 0) {
      setError('No active waves to generate audio from');
      analyticsService.trackError('audio_generate_no_waves', 'No active waves selected');
      return;
    }

    const startTime = Date.now();
    try {
      setIsGenerating(true);
      setError('');
      setAudioUrl(null);

      // Track the audio generation attempt
      analyticsService.trackAudioProcess('generate', duration);
      analyticsService.trackCustomDimension('audio_sample_rate', sampleRate);
      analyticsService.trackCustomDimension('audio_format', format);
      analyticsService.trackCustomDimension('active_waves_count', waves.filter(w => !w.muted).length);

      const result = await generateWaveform({
        waves,
        sampleRate,
        duration,
        format
      });

      const data = result.data as { success: boolean; waveform?: number[] | Float32Array };
      
      if (data.success) {
        const waveformData = new Float32Array(data.waveform as number[]);
        
        if (format === 'wav') {
          // Create WAV blob from the returned data
          const blob = new Blob([new Uint8Array(waveformData)], { type: 'audio/wav' });
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
        } else {
          // Convert raw data to WAV for playback
          const wavBlob = convertToWAV(waveformData, sampleRate);
          const url = URL.createObjectURL(wavBlob);
          setAudioUrl(url);
        }
        
        // Track successful audio generation
        const generationTime = Date.now() - startTime;
        analyticsService.trackPerformance('audio_generation', generationTime, `format:${format}`);
        analyticsService.trackFeatureUse('audio_generation_success', `${waves.filter(w => !w.muted).length}_waves`);
      } else {
        setError('Failed to generate audio');
        analyticsService.trackError('audio_generate_failed', 'Server returned failure');
      }
    } catch (error: any) {
      console.error('Audio generation error:', error);
      setError(error.message || 'Audio generation failed');
      analyticsService.trackError('audio_generate_exception', error?.message || 'Unknown error');
    } finally {
      setIsGenerating(false);
    }
  };

  const convertToWAV = (samples: Float32Array, sampleRate: number): Blob => {
    // Check if this is interleaved stereo data (even length, likely stereo)
    const isStereo = samples.length % 2 === 0;
    const numChannels = isStereo ? 2 : 1;
    const length = isStereo ? samples.length / 2 : samples.length;
    const bytesPerSample = 2; // 16-bit
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;
    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true); // stereo or mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Convert float samples to 16-bit PCM
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const sample = Math.max(-1, Math.min(1, samples[i]));
      const intSample = Math.floor(sample * 0x7FFF);
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const downloadAudio = () => {
    if (!audioUrl) return;
    
    analyticsService.trackFeatureUse('audio_download', format);
    
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `frequency-canvas-${Date.now()}.wav`;
    a.click();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Audio Generator</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        {!authService.isAuthenticated() && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 text-yellow-700 rounded">
            Please sign in to generate audio files.
          </div>
        )}

        {/* Configuration */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (seconds)
            </label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(Math.max(0.1, Math.min(9, parseFloat(e.target.value) || 1)))}
              min="0.1"
              max="9"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum: 9 seconds</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sample Rate (Hz)
            </label>
            <select
              value={sampleRate}
              onChange={(e) => setSampleRate(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={22050}>22,050 Hz</option>
              <option value={44100}>44,100 Hz (CD Quality)</option>
              <option value={48000}>48,000 Hz (Professional)</option>
              <option value={96000}>96,000 Hz (High Resolution)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Format
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as 'raw' | 'wav')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="wav">WAV (Recommended)</option>
              <option value="raw">Raw PCM Data</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="loopAudio"
              checked={loopAudio}
              onChange={(e) => setLoopAudio(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="loopAudio" className="ml-2 block text-sm text-gray-700">
              Loop audio in preview
            </label>
          </div>
        </div>

        {/* Wave Preview */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Active Waves ({waves.filter(w => !w.muted).length})</h3>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {waves.filter(w => !w.muted).map((wave) => (
              <div key={wave.id} className="flex items-center space-x-4 py-2 px-3 bg-gray-50 rounded">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: wave.color }}
                />
                <span className="font-mono text-sm">{wave.freq} Hz</span>
                <span className="font-mono text-sm">{wave.amp}%</span>
                <span className="font-mono text-sm">{wave.phase}°</span>
              </div>
            ))}
          </div>
          {waves.filter(w => !w.muted).length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No active waves. Unmute some waves to generate audio.
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex space-x-3 mb-4">
          <button
            onClick={handleGenerateAudio}
            disabled={isGenerating || !authService.isAuthenticated() || waves.filter(w => !w.muted).length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate Audio'}
          </button>
          
          {audioUrl && (
            <button
              onClick={downloadAudio}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Download
            </button>
          )}
        </div>

        {/* Audio Player */}
        {audioUrl && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Generated Audio</h3>
            <audio 
              src={audioUrl} 
              controls 
              loop={loopAudio}
              className="w-full"
              onError={() => setError('Failed to load audio for playback')}
            />
            <div className="mt-2 text-xs text-gray-500">
              Duration: {duration}s • Sample Rate: {sampleRate.toLocaleString()} Hz • Format: {format.toUpperCase()}
              {loopAudio && ' • Looping enabled'}
            </div>
          </div>
        )}

        {/* Loading */}
        {isGenerating && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Generating audio waveform...</span>
          </div>
        )}
      </div>
    </div>
  );
};